import {
  defineUtilityRegistry,
  type UtilityRouteDefinition,
} from '@saas-pages/shared/public/registry'
import {
  unitConverterPairRoutes,
  unitConverterRoute,
  type UnitConverterRouteKey,
} from '@/shared/unit-converter-routes'

type CalcdeckRouteKey =
  | 'percentage-calculator'
  | 'average-median-calculator'
  | 'fraction-calculator'
  | 'combinatorics-calculator'
  | 'vat-calculator'
  | UnitConverterRouteKey
  | 'ratio-proportion-calculator'
  | 'margin-markup-calculator'
  | 'discount-calculator'
  | 'random-number-generator'
  | 'dice-roller'
  | 'coin-flipper'

export const percentageCalculatorContent = {
  title: 'Процентный калькулятор',
  description:
    'Посчитайте процент от числа, долю одного числа от другого или изменение значения на процент. Все расчёты выполняются локально в браузере.',
  howTo: [
    'Выберите нужный режим расчёта: процент от числа, доля от числа или изменение на процент.',
    'Введите числа в поля калькулятора. Можно использовать точку или запятую как десятичный разделитель.',
    'Проверьте результат и формулу — они обновляются сразу после ввода.',
  ],
  faq: [
    {
      question: 'Какие расчёты поддерживает процентный калькулятор?',
      answer:
        'Калькулятор считает X процентов от Y, определяет, сколько процентов X составляет от Y, а также увеличивает или уменьшает число на заданный процент.',
    },
    {
      question: 'Данные отправляются на сервер?',
      answer:
        'Нет. Процентный калькулятор работает локально в браузере, введённые числа не отправляются на сервер.',
    },
    {
      question: 'Можно вводить дробные числа?',
      answer:
        'Да. Поддерживаются дробные числа с точкой или запятой, например 12.5 или 12,5.',
    },
  ],
} as const

export const percentageCalculatorRoute = {
  routeKey: 'percentage-calculator',
  path: '/procentnyj-kalkulyator',
  title: percentageCalculatorContent.title,
  description: percentageCalculatorContent.description,
  nav: {
    label: 'Проценты',
    category: 'calculator',
    order: 10,
  },
  seo: {
    title: 'Процентный калькулятор онлайн бесплатно - calcdeck.ru',
    description:
      'Бесплатный процентный калькулятор: X процентов от Y, сколько процентов X от Y, увеличение и уменьшение на процент. Работает локально в браузере без регистрации.',
    changefreq: 'monthly',
    priority: 1,
  },
  processing: {
    mode: 'local',
    label: 'Локально в браузере',
    title: 'Все расчёты выполняются прямо в браузере и не отправляются на сервер',
  },
  analyticsKey: 'percentage-calculator',
  availability: 'available',
  schema: {
    applicationCategory: 'UtilitiesApplication',
    howTo: percentageCalculatorContent.howTo,
    faq: percentageCalculatorContent.faq,
  },
  relatedTools: [
    {
      routeKey: 'vat-calculator',
      label: 'НДС и сумма прописью',
      description: 'Начисление и выделение НДС с суммами для документа.',
    },
    {
      routeKey: 'ratio-proportion-calculator',
      label: 'Отношения и пропорции',
      description: 'Решение пропорций, сокращение и масштабирование отношений.',
    },
    {
      routeKey: 'fraction-calculator',
      label: 'Калькулятор дробей',
      description: 'Действия с обыкновенными дробями, сокращение и смешанные числа.',
    },
    {
      routeKey: 'average-median-calculator',
      label: 'Среднее и медиана',
      description: 'Сводка по списку чисел: сумма, среднее, медиана, минимум и максимум.',
    },
  ],
} as const satisfies UtilityRouteDefinition<'percentage-calculator', CalcdeckRouteKey>

export const averageMedianCalculatorContent = {
  title: 'Калькулятор среднего и медианы',
  description:
    'Вставьте список чисел и сразу получите количество, сумму, среднее арифметическое, медиану, минимум, максимум и размах. Расчёт выполняется локально в браузере.',
  howTo: [
    'Вставьте числа в поле ввода. Поддерживаются пробелы, новые строки, запятые, точки с запятой и табы.',
    'Проверьте блок проигнорированных токенов, если в списке были подписи или некорректные значения.',
    'Скопируйте итоговую таблицу, чтобы перенести результаты в документ или таблицу.',
  ],
  faq: [
    {
      question: 'Как калькулятор считает медиану?',
      answer:
        'Список чисел сортируется по возрастанию. Для нечётного количества берётся центральное число, для чётного - среднее двух центральных чисел.',
    },
    {
      question: 'Можно использовать десятичную запятую?',
      answer:
        'Да. Значение вроде 15,5 считается дробным числом, когда запятая не выглядит разделителем списка. Десятичная точка тоже поддерживается.',
    },
    {
      question: 'Данные отправляются на сервер?',
      answer:
        'Нет. Список чисел обрабатывается локально в браузере и не отправляется на сервер.',
    },
  ],
} as const

export const averageMedianCalculatorRoute = {
  routeKey: 'average-median-calculator',
  path: '/srednee-mediana',
  title: averageMedianCalculatorContent.title,
  description: averageMedianCalculatorContent.description,
  nav: {
    label: 'Среднее и медиана',
    category: 'calculator',
    order: 20,
  },
  seo: {
    title: 'Калькулятор среднего и медианы онлайн бесплатно - calcdeck.ru',
    description:
      'Бесплатный калькулятор среднего и медианы: вставьте список чисел и получите сумму, количество, среднее, медиану, минимум, максимум и размах. Работает локально в браузере.',
    changefreq: 'monthly',
    priority: 0.95,
  },
  processing: {
    mode: 'local',
    label: 'Локально в браузере',
    title: 'Список чисел обрабатывается прямо в браузере и не отправляется на сервер',
  },
  analyticsKey: 'average-median-calculator',
  availability: 'available',
  schema: {
    applicationCategory: 'UtilitiesApplication',
    howTo: averageMedianCalculatorContent.howTo,
    faq: averageMedianCalculatorContent.faq,
  },
  relatedTools: [
    {
      routeKey: 'fraction-calculator',
      label: 'Калькулятор дробей',
      description: 'Действия с обыкновенными дробями, сокращение и смешанные числа.',
    },
    {
      routeKey: 'percentage-calculator',
      label: 'Процентный калькулятор',
      description: 'Процент от числа, доля в процентах и изменение значения на процент.',
    },
  ],
} as const satisfies UtilityRouteDefinition<'average-median-calculator', CalcdeckRouteKey>

export const fractionCalculatorContent = {
  title: 'Калькулятор дробей',
  description:
    'Складывайте, вычитайте, умножайте, делите и сокращайте обыкновенные дроби, а также переводите смешанные числа. Расчёт выполняется локально в браузере.',
  howTo: [
    'Выберите действие с дробями или режим перевода смешанного числа.',
    'Введите целые числители и знаменатели. Для смешанного числа также укажите целую часть.',
    'Получите сокращённый результат и проверьте компактные шаги расчёта.',
  ],
  faq: [
    {
      question: 'Калькулятор сокращает дроби?',
      answer:
        'Да. Результат каждого действия автоматически сокращается до несократимой дроби, а знак переносится в числитель.',
    },
    {
      question: 'Какие действия с дробями доступны?',
      answer:
        'Доступны сложение, вычитание, умножение, деление, сокращение, перевод смешанного числа в неправильную дробь и обратный перевод.',
    },
    {
      question: 'Данные отправляются на сервер?',
      answer:
        'Нет. Целочисленная арифметика выполняется локально в браузере, введённые значения не отправляются на сервер.',
    },
  ],
} as const

export const fractionCalculatorRoute = {
  routeKey: 'fraction-calculator',
  path: '/kalkulyator-drobej',
  title: fractionCalculatorContent.title,
  description: fractionCalculatorContent.description,
  nav: {
    label: 'Дроби',
    category: 'calculator',
    order: 30,
  },
  seo: {
    title: 'Калькулятор дробей онлайн с решением - calcdeck.ru',
    description:
      'Бесплатный калькулятор дробей: сложение, вычитание, умножение, деление, сокращение и смешанные числа. Точный локальный расчёт без регистрации.',
    changefreq: 'monthly',
    priority: 0.95,
  },
  processing: {
    mode: 'local',
    label: 'Локально в браузере',
    title: 'Дроби рассчитываются прямо в браузере и не отправляются на сервер',
  },
  analyticsKey: 'fraction-calculator',
  availability: 'available',
  schema: {
    applicationCategory: 'UtilitiesApplication',
    howTo: fractionCalculatorContent.howTo,
    faq: fractionCalculatorContent.faq,
  },
  relatedTools: [
    {
      routeKey: 'combinatorics-calculator',
      label: 'Комбинаторика',
      description: 'Точные сочетания и размещения с повторениями или без них.',
    },
    {
      routeKey: 'ratio-proportion-calculator',
      label: 'Отношения и пропорции',
      description: 'Решение пропорций, сокращение и масштабирование отношений.',
    },
    {
      routeKey: 'percentage-calculator',
      label: 'Процентный калькулятор',
      description: 'Процент от числа, доля в процентах и изменение значения на процент.',
    },
    {
      routeKey: 'average-median-calculator',
      label: 'Среднее и медиана',
      description: 'Сводка по списку чисел: сумма, среднее, медиана, минимум и максимум.',
    },
  ],
} as const satisfies UtilityRouteDefinition<'fraction-calculator', CalcdeckRouteKey>

export const combinatoricsCalculatorContent = {
  title: 'Калькулятор комбинаций: сочетания и размещения',
  description:
    'Выберите, важен ли порядок и разрешены ли повторения, чтобы точно посчитать сочетания или размещения. Значения обрабатываются локально в браузере.',
  howTo: [
    'Определите, важен ли порядок элементов и можно ли выбирать один элемент повторно.',
    'Выберите один из четырёх режимов и введите неотрицательные целые n и k от 0 до 10 000.',
    'Проверьте точное целое число, формулу и подстановку текущих значений: они обновляются сразу.',
  ],
  faq: [
    {
      question: 'Чем сочетания отличаются от размещений?',
      answer:
        'В размещениях порядок элементов важен, а в сочетаниях — нет. Например, пары АБ и БА считаются разными размещениями, но одним сочетанием.',
    },
    {
      question: 'Что означает «с повторениями»?',
      answer:
        'Один и тот же элемент можно выбирать несколько раз. Для размещений это даёт n в степени k, а для сочетаний используется C(n + k − 1, k).',
    },
    {
      question: 'Чем размещение отличается от перестановки?',
      answer:
        'Перестановка — частный случай размещения без повторений, когда выбираются все элементы и k равно n.',
    },
    {
      question: 'Значения и результат отправляются на сервер?',
      answer:
        'Нет. Расчёт выполняется в браузере, а значения n, k и результат не отправляются на сервер. Явное копирование записывает ответ в системный буфер обмена.',
    },
  ],
} as const

export const combinatoricsCalculatorRoute = {
  routeKey: 'combinatorics-calculator',
  path: '/kalkulyator-kombinatoriki',
  title: combinatoricsCalculatorContent.title,
  description: combinatoricsCalculatorContent.description,
  nav: {
    label: 'Комбинаторика',
    category: 'calculator',
    order: 35,
  },
  seo: {
    title: 'Калькулятор комбинаций: сочетания и размещения - calcdeck.ru',
    description:
      'Посчитайте число комбинаций онлайн: сочетания и размещения с повторениями и без. Точный ответ сразу, формула и подстановка значений, без регистрации.',
    changefreq: 'monthly',
    priority: 0.95,
  },
  processing: {
    mode: 'local',
    label: 'Локально в браузере',
    title: 'Значения n, k и точный результат рассчитываются в браузере и не отправляются на сервер',
  },
  analyticsKey: 'combinatorics-calculator',
  availability: 'available',
  schema: {
    applicationCategory: 'EducationalApplication',
    howTo: combinatoricsCalculatorContent.howTo,
    faq: combinatoricsCalculatorContent.faq,
  },
  relatedTools: [
    {
      routeKey: 'fraction-calculator',
      label: 'Калькулятор дробей',
      description: 'Точные действия с дробями, сокращение и смешанные числа.',
    },
    {
      routeKey: 'ratio-proportion-calculator',
      label: 'Отношения и пропорции',
      description: 'Решение пропорций, сокращение и масштабирование отношений.',
    },
  ],
} as const satisfies UtilityRouteDefinition<'combinatorics-calculator', CalcdeckRouteKey>

export const vatCalculatorContent = {
  title: 'Калькулятор НДС и сумма прописью',
  description:
    'Начислите или выделите НДС по выбранной ставке и получите суммы цифрами и прописью для документа. Расчёт выполняется локально в браузере.',
  howTo: [
    'Выберите режим: начислить НДС, выделить НДС или записать сумму прописью без налоговой операции.',
    'Введите сумму в рублях и копейках. Для операции с НДС самостоятельно выберите одну из доступных ставок.',
    'Проверьте суммы без НДС, НДС и с НДС: они обновляются сразу. Затем скопируйте готовый расчёт.',
  ],
  faq: [
    {
      question: 'Как начисляется НДС на сумму без налога?',
      answer:
        'Калькулятор умножает сумму без налога на выбранную ставку, один раз округляет НДС до копейки по правилу half-up и складывает две суммы.',
    },
    {
      question: 'Как выделяется НДС из суммы с налогом?',
      answer:
        'НДС рассчитывается как сумма с налогом, умноженная на ставку и разделённая на 100 плюс ставка. После округления сумма без НДС получается вычитанием.',
    },
    {
      question: 'Калькулятор выбирает подходящую ставку?',
      answer:
        'Нет. Инструмент выполняет только арифметику по ставке, которую выбрал пользователь. Применимость ставки нужно проверить по данным ФНС или у специалиста.',
    },
    {
      question: 'Сумма и текст прописью отправляются на сервер?',
      answer:
        'Нет. Расчёт выполняется в браузере, введённая сумма и текст прописью не отправляются на сервер.',
    },
  ],
} as const

export const vatCalculatorRoute = {
  routeKey: 'vat-calculator',
  path: '/kalkulyator-nds',
  title: vatCalculatorContent.title,
  description: vatCalculatorContent.description,
  nav: {
    label: 'НДС и сумма прописью',
    category: 'calculator',
    order: 38,
  },
  seo: {
    title: 'Калькулятор НДС и сумма прописью онлайн - calcdeck.ru',
    description:
      'Начислите или выделите НДС по ставке 0%, 5%, 7%, 10% или 22%, получите суммы цифрами и прописью. Точный локальный расчёт для документа.',
    changefreq: 'monthly',
    priority: 0.95,
  },
  processing: {
    mode: 'local',
    label: 'Локально в браузере',
    title: 'Введённая сумма и текст прописью рассчитываются в браузере и не отправляются на сервер',
  },
  analyticsKey: 'vat-calculator',
  availability: 'available',
  schema: {
    applicationCategory: 'FinanceApplication',
    howTo: vatCalculatorContent.howTo,
    faq: vatCalculatorContent.faq,
  },
  relatedTools: [
    {
      routeKey: 'percentage-calculator',
      label: 'Процентный калькулятор',
      description: 'Процент от числа, доля в процентах и изменение значения.',
    },
  ],
} as const satisfies UtilityRouteDefinition<'vat-calculator', CalcdeckRouteKey>

export const ratioProportionCalculatorContent = {
  title: 'Калькулятор пропорций и соотношений',
  description:
    'Решайте пропорции, сокращайте отношения и масштабируйте их на заданный множитель. Расчёт выполняется локально в браузере.',
  howTo: [
    'Выберите режим: решение пропорции, сокращение отношения или масштабирование.',
    'Введите целые или десятичные значения с точкой либо запятой.',
    'Проверьте автоматически обновлённый результат и формулу расчёта, затем при необходимости скопируйте ответ.',
  ],
  faq: [
    {
      question: 'Как решить пропорцию A:B = C:X?',
      answer:
        'Калькулятор применяет формулу X = B × C ÷ A. Значение A не может быть нулём, потому что деление на ноль невозможно.',
    },
    {
      question: 'Как посчитать соотношение двух чисел?',
      answer:
        'Разделите оба числа на их наибольший общий делитель. В режиме сокращения калькулятор делает это сам: 150 и 200 дают соотношение 3:4.',
    },
    {
      question: 'Можно сокращать десятичные отношения?',
      answer:
        'Да. Калькулятор точно приводит десятичные значения к общему целочисленному масштабу и сокращает их по наибольшему общему делителю. Например, 1,5:2,25 превращается в 2:3.',
    },
    {
      question: 'Как отображаются длинные десятичные результаты?',
      answer:
        'Результаты решения и масштабирования показываются максимум с десятью знаками после запятой. Если цифры отброшены, рядом появляется явная пометка об округлении.',
    },
    {
      question: 'Данные отправляются на сервер?',
      answer:
        'Нет. Все вычисления выполняются локально в браузере, введённые значения не отправляются на сервер.',
    },
  ],
} as const

export const ratioProportionCalculatorRoute = {
  routeKey: 'ratio-proportion-calculator',
  path: '/kalkulyator-proporcij',
  title: ratioProportionCalculatorContent.title,
  description: ratioProportionCalculatorContent.description,
  nav: {
    label: 'Пропорции',
    category: 'calculator',
    order: 40,
  },
  seo: {
    title: 'Калькулятор пропорций: решить пропорцию онлайн - calcdeck.ru',
    description:
      'Решите пропорцию онлайн и посчитайте соотношение: найдите неизвестный член, сократите или масштабируйте отношение. Результат сразу, с формулой.',
    changefreq: 'monthly',
    priority: 0.95,
  },
  processing: {
    mode: 'local',
    label: 'Локально в браузере',
    title: 'Отношения и пропорции рассчитываются прямо в браузере и не отправляются на сервер',
  },
  analyticsKey: 'ratio-proportion-calculator',
  availability: 'available',
  schema: {
    applicationCategory: 'UtilitiesApplication',
    howTo: ratioProportionCalculatorContent.howTo,
    faq: ratioProportionCalculatorContent.faq,
  },
  relatedTools: [
    {
      routeKey: 'combinatorics-calculator',
      label: 'Комбинаторика',
      description: 'Точные сочетания и размещения с повторениями или без них.',
    },
    {
      routeKey: 'percentage-calculator',
      label: 'Процентный калькулятор',
      description: 'Процент от числа, доля в процентах и изменение значения на процент.',
    },
    {
      routeKey: 'fraction-calculator',
      label: 'Калькулятор дробей',
      description: 'Действия с обыкновенными дробями, сокращение и смешанные числа.',
    },
    {
      routeKey: 'average-median-calculator',
      label: 'Среднее и медиана',
      description: 'Сводка по списку чисел: сумма, среднее, медиана, минимум и максимум.',
    },
  ],
} as const satisfies UtilityRouteDefinition<'ratio-proportion-calculator', CalcdeckRouteKey>

export const marginMarkupCalculatorContent = {
  title: 'Калькулятор маржи и наценки',
  description:
    'Рассчитайте себестоимость, цену продажи, прибыль, маржу и наценку по двум известным показателям. Все вычисления выполняются локально в браузере.',
  howTo: [
    'Выберите два известных показателя: себестоимость, цену продажи, прибыль, маржу или наценку.',
    'Введите значения с точкой или запятой: результат обновится сразу.',
    'Сравните маржу с долей прибыли в цене продажи и наценку с прибавкой к себестоимости.',
  ],
  faq: [
    {
      question: 'Чем маржа отличается от наценки?',
      answer:
        'Маржа — это прибыль, делённая на цену продажи. Наценка — прибыль, делённая на себестоимость. Поэтому при одной сделке эти проценты обычно различаются.',
    },
    {
      question: 'Как округляются результаты?',
      answer:
        'Внутри калькулятора десятичные значения рассчитываются точно. Денежные показатели показываются максимум с двумя знаками после точки, проценты — с четырьмя; округлённые значения помечаются.',
    },
    {
      question: 'Что происходит с нулевыми и отрицательными значениями?',
      answer:
        'Ноль и отрицательные суммы разрешены как арифметические значения. При нулевой цене продажи маржа не определена, а при нулевой себестоимости не определена наценка. Если такой процент выбран как исходное значение, расчёт блокируется вместо частичного результата.',
    },
    {
      question: 'Можно рассчитать суммы только по марже и наценке?',
      answer:
        'Нет. Два процента задают соотношение, но не денежный масштаб. Для расчёта сумм нужен хотя бы один денежный показатель.',
    },
    {
      question: 'Введённые суммы отправляются на сервер?',
      answer:
        'Нет. Вычисления выполняются локально в браузере. Аналитика фиксирует только выбранную пару показателей и исход операции, без введённых чисел.',
    },
  ],
} as const

export const marginMarkupCalculatorRoute = {
  routeKey: 'margin-markup-calculator',
  path: '/kalkulyator-marzhi-i-natsenki',
  title: marginMarkupCalculatorContent.title,
  description: marginMarkupCalculatorContent.description,
  nav: {
    label: 'Маржа и наценка',
    category: 'calculator',
    order: 45,
  },
  seo: {
    title: 'Калькулятор маржи и наценки онлайн - calcdeck.ru',
    description:
      'Рассчитайте прибыль, маржу и наценку по себестоимости и цене продажи или выберите другую пару показателей. Формулы и локальный расчёт.',
    changefreq: 'monthly',
    priority: 0.95,
  },
  processing: {
    mode: 'local',
    label: 'Локально в браузере',
    title: 'Суммы и проценты рассчитываются в браузере и не отправляются на сервер',
  },
  analyticsKey: 'margin-markup-calculator',
  availability: 'available',
  schema: {
    applicationCategory: 'FinanceApplication',
    howTo: marginMarkupCalculatorContent.howTo,
    faq: marginMarkupCalculatorContent.faq,
  },
  relatedTools: [
    {
      routeKey: 'percentage-calculator',
      label: 'Процентный калькулятор',
      description: 'Процент от числа, доля в процентах и изменение значения.',
    },
    {
      routeKey: 'ratio-proportion-calculator',
      label: 'Отношения и пропорции',
      description: 'Решение пропорций, сокращение и масштабирование отношений.',
    },
  ],
} as const satisfies UtilityRouteDefinition<'margin-markup-calculator', CalcdeckRouteKey>

export const discountCalculatorContent = {
  title: 'Калькулятор скидки',
  description:
    'Рассчитайте итоговую цену, экономию и эффективную скидку для одного или нескольких последовательных уменьшений цены. Все вычисления выполняются локально в браузере.',
  howTo: [
    'Выберите прямой расчёт, восстановление исходной цены или определение скидки по старой и новой цене.',
    'Введите цены и от одной до трёх последовательных скидок. Поддерживаются точка и запятая.',
    'Проверьте итоговую цену, экономию, эффективную скидку и формулу: они обновляются сразу.',
  ],
  faq: [
    {
      question: 'Почему скидки 20% и 15% не дают 35%?',
      answer:
        'Каждая следующая скидка применяется к уже уменьшенной цене. После скидки 20% остаётся 80% цены, затем 15% вычитаются из этого остатка: 0,8 × 0,85 = 0,68, поэтому эффективная скидка равна 32%.',
    },
    {
      question: 'Как округляются цены и проценты?',
      answer:
        'Калькулятор хранит точные десятичные дроби и не округляет промежуточные шаги. Для отображения деньги округляются до двух знаков, проценты — до четырёх; половины округляются от нуля.',
    },
    {
      question: 'Можно восстановить цену до скидки?',
      answer:
        'Да. Укажите итоговую цену и известные последовательные скидки. Скидку 100% обратить нельзя, потому что нулевая итоговая цена не определяет единственную исходную цену.',
    },
    {
      question: 'Введённые цены отправляются на сервер?',
      answer:
        'Нет. Расчёты выполняются локально в браузере. Аналитика фиксирует только режим и исход операции, без цен и процентов.',
    },
  ],
} as const

export const discountCalculatorRoute = {
  routeKey: 'discount-calculator',
  path: '/kalkulyator-skidki',
  title: discountCalculatorContent.title,
  description: discountCalculatorContent.description,
  nav: {
    label: 'Скидка',
    category: 'calculator',
    order: 47,
  },
  seo: {
    title: 'Калькулятор скидки онлайн: итоговая цена и экономия - calcdeck.ru',
    description:
      'Рассчитайте цену после одной или нескольких скидок, экономию, эффективный процент или исходную цену. Формула и локальный расчёт.',
    changefreq: 'monthly',
    priority: 0.95,
  },
  processing: {
    mode: 'local',
    label: 'Локально в браузере',
    title: 'Цены и скидки рассчитываются в браузере и не отправляются на сервер',
  },
  analyticsKey: 'discount-calculator',
  availability: 'available',
  schema: {
    applicationCategory: 'FinanceApplication',
    howTo: discountCalculatorContent.howTo,
    faq: discountCalculatorContent.faq,
  },
  relatedTools: [
    {
      routeKey: 'percentage-calculator',
      label: 'Процентный калькулятор',
      description: 'Процент от числа, доля в процентах и изменение значения.',
    },
    {
      routeKey: 'margin-markup-calculator',
      label: 'Маржа и наценка',
      description: 'Расчёт цены, прибыли, маржи и наценки по двум показателям.',
    },
  ],
} as const satisfies UtilityRouteDefinition<'discount-calculator', CalcdeckRouteKey>

export const randomNumberGeneratorContent = {
  title: 'Генератор случайных чисел',
  description:
    'Сгенерируйте одно или несколько целых или десятичных чисел в заданном диапазоне. Выборка выполняется локально средствами браузера.',
  howTo: [
    'Выберите целые или десятичные числа и задайте включительные границы диапазона.',
    'Укажите количество значений, точность для десятичных чисел и при необходимости включите уникальность.',
    'Нажмите «Сгенерировать», проверьте список и скопируйте его одной командой.',
  ],
  faq: [
    {
      question: 'Границы диапазона включаются в выборку?',
      answer:
        'Для целых чисел минимум и максимум входят в диапазон. Для десятичных чисел доступны только точки конечной сетки с выбранным количеством знаков после точки: исходная граница входит в выборку лишь при совпадении с этой точностью.',
    },
    {
      question: 'Как работает генерация без повторов?',
      answer:
        'Инструмент заранее считает число доступных значений и блокирует невозможную уникальную выборку. Поэтому генерация не зацикливается при заполнении диапазона.',
    },
    {
      question: 'Числа генерируются на сервере?',
      answer:
        'Нет. Генерация происходит локально средствами браузера. Инструмент не заявляет криптографическую стойкость и не предназначен для ключей или лотерей.',
    },
  ],
} as const

export const randomNumberGeneratorRoute = {
  routeKey: 'random-number-generator',
  path: '/generator-sluchajnyh-chisel',
  title: randomNumberGeneratorContent.title,
  description: randomNumberGeneratorContent.description,
  nav: {
    label: 'Случайные числа',
    category: 'calculator',
    order: 50,
  },
  seo: {
    title: 'Генератор случайных чисел онлайн - calcdeck.ru',
    description:
      'Бесплатный генератор случайных чисел: целые и десятичные значения, несколько чисел и выборка без повторов. Работает локально в браузере.',
    changefreq: 'monthly',
    priority: 0.95,
  },
  processing: {
    mode: 'local',
    label: 'Локально в браузере',
    title: 'Случайные числа генерируются средствами браузера и не отправляются на сервер',
  },
  analyticsKey: 'random-number-generator',
  availability: 'available',
  schema: {
    applicationCategory: 'UtilitiesApplication',
    howTo: randomNumberGeneratorContent.howTo,
    faq: randomNumberGeneratorContent.faq,
  },
  relatedTools: [
    {
      routeKey: 'dice-roller',
      label: 'Бросить кубик',
      description: 'Броски d4–d100, формулы вида 3d6+2 и общий итог.',
    },
    {
      routeKey: 'coin-flipper',
      label: 'Подбросить монетку',
      description: 'Один бросок или серия со счётом орла и решки.',
    },
    {
      routeKey: 'average-median-calculator',
      label: 'Среднее и медиана',
      description: 'Посчитайте статистику для полученного списка чисел.',
    },
  ],
} as const satisfies UtilityRouteDefinition<'random-number-generator', CalcdeckRouteKey>

export const diceRollerContent = {
  title: 'Бросить кубик онлайн',
  description:
    'Бросайте d4, d6, d8, d10, d12, d20 или d100, задавайте число кубиков и модификатор формулой вроде 3d6+2. Каждый бросок выполняется локально в браузере.',
  howTo: [
    'Выберите тип кубика, количество и модификатор или введите формулу вида 3d6+2.',
    'Нажмите «Бросить кубики», чтобы выполнить новый независимый бросок каждого кубика.',
    'Проверьте отдельные значения и итог с учётом положительного или отрицательного модификатора.',
  ],
  faq: [
    {
      question: 'Какие кубики поддерживаются?',
      answer: 'На одной странице доступны d4, d6, d8, d10, d12, d20 и d100. Можно бросить от 1 до 100 одинаковых кубиков.',
    },
    {
      question: 'Как записать формулу 3d6+2?',
      answer: 'Число перед d означает количество кубиков, число после d — количество граней, а +2 — модификатор итога. Отрицательный модификатор записывается, например, как 2d20-1.',
    },
    {
      question: 'Можно использовать бросок для азартных игр или официального розыгрыша?',
      answer: 'Инструмент предназначен для обычных игровых и бытовых сценариев. Он не заявляет true random, проверяемый результат или пригодность для азартных и юридически значимых розыгрышей.',
    },
    {
      question: 'Результаты отправляются на сервер?',
      answer: 'Нет. Броски создаются локально средствами браузера и не отправляются на сервер.',
    },
  ],
} as const

export const diceRollerRoute = {
  routeKey: 'dice-roller',
  path: '/brosit-kubik',
  title: diceRollerContent.title,
  description: diceRollerContent.description,
  nav: { label: 'Бросить кубик', category: 'calculator', order: 60 },
  seo: {
    title: 'Бросить кубик онлайн: d6, d20 и формула 3d6 - calcdeck.ru',
    description: 'Бесплатный бросок кубиков d4–d100 онлайн: несколько кубиков, модификатор, формулы 3d6+2, отдельные результаты и итог. Работает локально.',
    changefreq: 'monthly',
    priority: 0.95,
  },
  processing: {
    mode: 'local',
    label: 'Локально в браузере',
    title: 'Броски кубиков создаются средствами браузера и не отправляются на сервер',
  },
  analyticsKey: 'dice-roller',
  availability: 'available',
  schema: {
    applicationCategory: 'UtilitiesApplication',
    howTo: diceRollerContent.howTo,
    faq: diceRollerContent.faq,
  },
  relatedTools: [
    {
      routeKey: 'coin-flipper',
      label: 'Подбросить монетку',
      description: 'Один бросок или ограниченная серия со счётом сторон.',
    },
    {
      routeKey: 'random-number-generator',
      label: 'Генератор случайных чисел',
      description: 'Одно или несколько чисел из заданного диапазона.',
    },
  ],
} as const satisfies UtilityRouteDefinition<'dice-roller', CalcdeckRouteKey>

export const coinFlipperContent = {
  title: 'Подбросить монетку онлайн',
  description:
    'Получите орла или решку одним броском либо запустите серию до 100 бросков с последовательностью и счётом сторон. Всё выполняется локально в браузере.',
  howTo: [
    'Выберите один бросок или укажите размер серии от 1 до 100.',
    'Нажмите «Подбросить монетку» или «Запустить серию», чтобы получить новый результат.',
    'Для серии проверьте последовательность, число орлов и число решек.',
  ],
  faq: [
    {
      question: 'Можно подбросить монетку несколько раз?',
      answer: 'Да. Доступны один бросок и серия от 1 до 100 бросков. Результат показывает порядок выпадений и общий счёт орла и решки.',
    },
    {
      question: 'Орёл и решка выпадают с одинаковой вероятностью?',
      answer: 'Для каждого броска инструмент выбирает одно из двух значений равновероятно средствами браузера. Короткая серия при этом не обязана делиться поровну.',
    },
    {
      question: 'Подходит ли монетка для ставок или официальных решений?',
      answer: 'Нет. Инструмент не заявляет true random, аудит результата или пригодность для азартных и юридически значимых решений.',
    },
    {
      question: 'Последовательность отправляется на сервер?',
      answer: 'Нет. Все броски выполняются локально в браузере, последовательность не отправляется на сервер.',
    },
  ],
} as const

export const coinFlipperRoute = {
  routeKey: 'coin-flipper',
  path: '/monetka',
  title: coinFlipperContent.title,
  description: coinFlipperContent.description,
  nav: { label: 'Монетка', category: 'calculator', order: 70 },
  seo: {
    title: 'Подбросить монетку онлайн: орёл или решка - calcdeck.ru',
    description: 'Бесплатная монетка онлайн: один бросок или серия до 100 раз, последовательность и счёт орла и решки. Работает локально в браузере.',
    changefreq: 'monthly',
    priority: 0.95,
  },
  processing: {
    mode: 'local',
    label: 'Локально в браузере',
    title: 'Броски монетки создаются средствами браузера и не отправляются на сервер',
  },
  analyticsKey: 'coin-flipper',
  availability: 'available',
  schema: {
    applicationCategory: 'UtilitiesApplication',
    howTo: coinFlipperContent.howTo,
    faq: coinFlipperContent.faq,
  },
  relatedTools: [
    {
      routeKey: 'dice-roller',
      label: 'Бросить кубик',
      description: 'Кубики d4–d100, несколько бросков и модификатор.',
    },
    {
      routeKey: 'random-number-generator',
      label: 'Генератор случайных чисел',
      description: 'Одно или несколько чисел из заданного диапазона.',
    },
  ],
} as const satisfies UtilityRouteDefinition<'coin-flipper', CalcdeckRouteKey>

export const calcdeckUtilityRegistry = defineUtilityRegistry([
  percentageCalculatorRoute,
  averageMedianCalculatorRoute,
  fractionCalculatorRoute,
  combinatoricsCalculatorRoute,
  vatCalculatorRoute,
  ratioProportionCalculatorRoute,
  marginMarkupCalculatorRoute,
  discountCalculatorRoute,
  randomNumberGeneratorRoute,
  diceRollerRoute,
  coinFlipperRoute,
  unitConverterRoute,
  ...unitConverterPairRoutes,
])

export type RouteKey = (typeof calcdeckUtilityRegistry)[number]['routeKey']
